/**
 * GP-02 bag view: Pip's bag with `cap` slots. Collected tiles fly to their slot in ~200 ms
 * (targets re-computed every frame, so grouped inserts and slides never break a flight).
 * A set of three pops once all three have landed; the rest slide left afterwards.
 * BagLock (rules v2): `locked` padlocked slots sit at the right end until the first ticket line
 * completes; items only fill the `cap` open slots.
 */
import * as Phaser from 'phaser';
import { app } from '../game/context.js';
import type { PlayLayout } from '../game/layout.js';
import { dur } from '../fx/motion.js';
import type { TileView } from './board.js';
import { itemX, lockedSlots, slotX, slotsShown, type BagGeom, type BagShape } from './bagSlots.js';
import { LockedSlot } from './lock.js';

const FLIGHT_MS = 200;

interface Item {
  tile: number;
  view: TileView;
  from: { x: number; y: number; s: number };
  start: number;
  /** hold before the flight when `start` is still unknown (0: set on the first frame) */
  delay: number;
  flying: boolean;
  popping: boolean;
  landed: Promise<void>;
  land: () => void;
}

const easeOut = (k: number) => 1 - Math.pow(1 - k, 3);

export class BagView {
  private items = new Map<number, Item>();
  /** display order (core bag order, with not-yet-popped sets kept in place) */
  private order: number[] = [];
  private slots: Phaser.GameObjects.Image[] = [];
  private body: Phaser.GameObjects.Image;
  private pending = new Set<Promise<unknown>>();
  cap: number;
  /** bagLock: slots still locked (drawn padlocked after the open ones) */
  locked: number;
  private locks: LockedSlot[] = [];
  /** unlocked in core, padlock animation not played yet */
  private opening: LockedSlot[] = [];
  private popping = 0;
  private slotSize = 0;
  private area: { x: number; y: number; w: number; h: number };
  private now = 0;

  constructor(
    private scene: Phaser.Scene,
    lay: PlayLayout,
    cap: number,
    pipW: number,
    locked = 0,
  ) {
    this.cap = cap;
    this.locked = locked;
    const b = lay.bag;
    this.area = { x: b.x + pipW * 0.74, y: b.y, w: b.w - pipW * 0.74, h: b.h };
    const f = app().art.get('chr_pip_bag_body');
    this.body = scene.add.image(0, 0, f.texture, f.frame).setDepth(3000);
    this.layoutBody(false);
  }

  get tileScale(): number {
    return this.slotSize / (this.anyTileSize() || 1);
  }

  private anyTileSize(): number {
    for (const it of this.items.values()) return it.view.size;
    return 0;
  }

  private layoutBody(animate: boolean): void {
    const a = this.area;
    const maxSlot = a.h * 0.7;
    const shown = slotsShown(this);
    this.slotSize = Math.min(maxSlot, (a.w * 0.92) / shown);
    const bodyW = this.slotSize * shown + this.slotSize * 0.5;
    const bodyH = this.slotSize * 1.42;
    const cx = a.x + a.w / 2;
    const cy = a.y + a.h / 2;
    const d = animate ? dur(260) : 0;
    const sx = bodyW / this.body.width;
    const sy = bodyH / this.body.height;
    if (d) this.scene.tweens.add({ targets: this.body, x: cx, y: cy, scaleX: sx, scaleY: sy, duration: d, ease: 'Back.easeOut' });
    else this.body.setPosition(cx, cy).setScale(sx, sy);
    const sf = app().art.get('ui_play_slot');
    // every drawn slot has the plain slot image; locked ones get a padlocked shutter on top
    while (this.slots.length < shown) this.slots.push(this.scene.add.image(0, 0, sf.texture, sf.frame).setDepth(3001).setAlpha(0.9));
    const g = this.geom;
    this.slots.forEach((s, i) => {
      const p = { x: slotX(g, i), y: cy };
      s.setVisible(i < shown);
      s.setDisplaySize(this.slotSize * 0.94, this.slotSize * 0.94);
      if (d) this.scene.tweens.add({ targets: s, x: p.x, y: p.y, duration: d, ease: 'Back.easeOut' });
      else s.setPosition(p.x, p.y);
    });
    while (this.locks.length < this.locked) this.locks.push(new LockedSlot(this.scene));
    lockedSlots(this).forEach((i, k) => this.locks[k]!.place(slotX(g, i), cy, this.slotSize, d));
  }

  private get geom(): BagGeom {
    return { cap: this.cap, locked: this.locked, slot: this.slotSize, cx: this.area.x + this.area.w / 2 };
  }

  /** Centre of the k-th item in the bag (open slots only). */
  slotPos(i: number): { x: number; y: number } {
    return { x: itemX(this.geom, i, this.order.length), y: this.area.y + this.area.h / 2 };
  }

  /** Centre of the first locked slot (null when none is locked). */
  get lockPos(): { x: number; y: number } | null {
    return this.locked ? { x: slotX(this.geom, this.cap), y: this.area.y + this.area.h / 2 } : null;
  }

  /** Padlocks on screen (test hooks): still locked, or unlocked in core with the animation to come or playing. */
  get locksShown(): number {
    return this.locks.length + this.opening.length + this.popping;
  }
  get bodyTop(): number {
    return this.area.y + this.area.h / 2 - this.slotSize * 0.71;
  }

  get center(): { x: number; y: number } {
    return { x: this.area.x + this.area.w / 2, y: this.area.y + this.area.h / 2 };
  }

  /**
   * A collected tile starts its flight; `bag` is the core bag snapshot after the insert (a
   * glued pair's partner may briefly make it cap + 1 long). `delayMs` holds the tile on its
   * spot first (a glued partner follows the tapped tile).
   */
  receive(tile: number, view: TileView, bag: number[], delayMs = 0): Promise<void> {
    let land!: () => void;
    const landed = new Promise<void>((r) => (land = r));
    view.setDepth(5000 + bag.indexOf(tile));
    const it: Item = { tile, view, from: { x: view.x, y: view.y, s: view.scale }, start: this.now ? this.now + delayMs : 0, delay: delayMs, flying: true, popping: false, landed, land };
    this.items.set(tile, it);
    this.setOrder(bag);
    if (!dur(FLIGHT_MS)) this.finishFlight(it);
    this.track(landed);
    return landed;
  }

  private setOrder(bag: number[]): void {
    const res = bag.filter((t) => this.items.has(t));
    // sets that core already removed keep their slots until they pop
    this.order.forEach((t, idx) => {
      if (this.items.get(t)?.popping && !res.includes(t)) res.splice(Math.min(idx, res.length), 0, t);
    });
    this.order = res;
    this.relayout();
  }

  private relayout(): void {
    const d = dur(140);
    this.order.forEach((t, i) => {
      const it = this.items.get(t);
      if (!it || it.flying) return;
      const p = this.slotPos(i);
      it.view.setDepth(5000 + i);
      if (it.view.x === p.x && it.view.y === p.y) return;
      if (!d) it.view.setPosition(p.x, p.y);
      else this.scene.tweens.add({ targets: it.view, x: p.x, y: p.y, duration: d, ease: 'Cubic.easeOut' });
    });
  }

  private finishFlight(it: Item): void {
    it.flying = false;
    const p = this.slotPos(Math.max(0, this.order.indexOf(it.tile)));
    it.view.setPosition(p.x, p.y).setScale(this.slotSize / it.view.size);
    it.land();
    if (dur(1)) {
      // landing squash
      const s = it.view.scaleX;
      this.scene.tweens.add({ targets: it.view, scaleX: s * 1.12, scaleY: s * 0.88, duration: 60, yoyo: true, ease: 'Sine.easeOut' });
    }
  }

  /** Per-frame flight integration (retargets to the current slot every frame). */
  update(time: number): void {
    this.now = time;
    const d = dur(FLIGHT_MS);
    for (const it of this.items.values()) {
      if (!it.flying) continue;
      if (it.start === 0) it.start = time + it.delay;
      const k = d ? Math.max(0, Math.min(1, (time - it.start) / d)) : 1;
      if (k >= 1) {
        this.finishFlight(it);
        continue;
      }
      const p = this.slotPos(Math.max(0, this.order.indexOf(it.tile)));
      const e = easeOut(k);
      const arc = Math.sin(Math.PI * k) * this.slotSize * 0.6;
      const s1 = this.slotSize / it.view.size;
      it.view.setPosition(it.from.x + (p.x - it.from.x) * e, it.from.y + (p.y - it.from.y) * e - arc);
      it.view.setScale(it.from.s + (s1 - it.from.s) * e);
    }
  }

  /**
   * Pop a set once all its tiles have landed. `onPop(x, y)` fires at the burst (serve the
   * ticket, float coins). Resolves when the remaining items have slid left.
   */
  pop(tiles: number[], onPop: (x: number, y: number) => void): Promise<void> {
    const its = tiles.map((t) => this.items.get(t)).filter((x): x is Item => !!x);
    for (const it of its) it.popping = true;
    const job = Promise.all(its.map((it) => it.landed)).then(
      () =>
        new Promise<void>((resolve) => {
          const cx = its.reduce((a, it) => a + it.view.x, 0) / Math.max(1, its.length);
          const cy = its[0]?.view.y ?? this.center.y;
          const d = dur(170);
          const done = () => {
            for (const it of its) {
              it.view.destroy();
              this.items.delete(it.tile);
            }
            this.order = this.order.filter((t) => !tiles.includes(t));
            this.relayout();
            resolve();
          };
          onPop(cx, cy);
          if (!d) return done();
          // squeeze into the middle, flash up, vanish
          for (const it of its) {
            this.scene.tweens.add({
              targets: it.view,
              x: cx,
              scale: it.view.scale * 1.25,
              duration: d * 0.55,
              ease: 'Back.easeIn',
            });
            this.scene.tweens.add({ targets: it.view, scale: 0, alpha: 0, delay: d * 0.55, duration: d * 0.45, ease: 'Cubic.easeIn' });
          }
          this.scene.time.delayedCall(d, done);
        }),
    );
    this.track(job);
    return job;
  }

  /** Remove a tile from the bag for undo/rescue; returns its view to fly back to the board. */
  release(tile: number, bag: number[]): TileView | null {
    const it = this.items.get(tile);
    if (!it) return null;
    this.items.delete(tile);
    it.flying = false;
    it.land();
    this.order = this.order.filter((t) => t !== tile);
    this.setOrder(bag);
    return it.view;
  }

  /**
   * Extra slot or rescue grow the open part (locked slots stay locked, still at the right end).
   * bagLock: when slots unlock the layout switches at once (the bag keeps its width, so nothing
   * moves) but their padlocks stay drawn until `openLocks()` plays.
   */
  reshape(s: BagShape): void {
    if (s.locked < this.locked) this.opening.push(...this.locks.splice(s.locked));
    this.cap = s.cap;
    this.locked = s.locked;
    this.layoutBody(true);
    this.relayout();
  }

  /** Play the unlock: each padlock springs open; `onPop` fires as it comes off. */
  openLocks(onPop: ((x: number, y: number) => void) | null): Promise<void> {
    const jobs = this.opening.map((l) => l.open(onPop));
    this.popping += this.opening.length;
    this.opening = [];
    const job = Promise.all(jobs).then(() => {
      this.popping -= jobs.length;
    });
    this.track(job);
    return job;
  }

  /** Mechanic intro / attention: the padlocks wiggle. */
  wiggleLocks(): void {
    for (const l of this.locks) l.wiggle();
  }

  /** Visible bag fill (for Pip's worried state). */
  get fill(): number {
    let n = 0;
    for (const it of this.items.values()) if (!it.popping) n++;
    return n;
  }

  private track(p: Promise<unknown>): void {
    this.pending.add(p);
    void p.finally(() => this.pending.delete(p));
  }

  /** Resolves when every flight and pop in progress has finished. */
  async idle(): Promise<void> {
    while (this.pending.size) await Promise.all([...this.pending]);
  }

  shake(): void {
    const d = dur(360);
    if (!d) return;
    this.scene.tweens.add({ targets: this.body, angle: { from: -4, to: 4 }, duration: d / 6, yoyo: true, repeat: 2, onComplete: () => this.body.setAngle(0) });
  }

  views(): TileView[] {
    return [...this.items.values()].map((i) => i.view);
  }
}
